import React, { useEffect, useState, useMemo } from 'react';
import { NavBar } from '@components/NavBar/NavBar';
import { SideBar } from '@components/SideBar/SideBar';
import { AppContainer, ContentContainer, Spinner, WrapperLoading } from './App.styles';
import { Footer } from '@components/Footer/Footer';
import { MainContent } from '@components/Main/Main';

import { useAppSelector } from '@app/hooks';
import { Helmet } from 'react-helmet';
import useInjectScripts from '@/hooks/useInjectScripts';
import { NewRelicConsent } from '@/components/Layout/NewRelicConsent/NewRelicConsent';
import { initializeNewRelic, updateNewRelicUser, setCustomNewRelicAttribute } from '@/config/newrelic';
import { useDispatch } from 'react-redux';
import { updateGlanceSettings } from '../reducers/TemplateConfig';
import { updateLoanApplication } from '../reducers/LoanApplication';
import { setIsOpenResume } from '../reducers/currentPage';

const App: React.FC = () => {
  const TemplateConfig = useAppSelector((state) => state.TemplateConfig);
  const { value } = useAppSelector((state) => state.currentPage);
  const glanceStatus = useAppSelector((state) => (state.TemplateConfig as any)?.glanceSettings);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  const isTemplateConfig = Boolean(TemplateConfig && Object.keys(TemplateConfig).length > 0);
  const favicon = TemplateConfig?.globalTheme?.favicon || '';
  const isLoanDepot = TemplateConfig?.lenderName === 'loanDepot';
  const htmlScripts = useMemo(() => TemplateConfig?.pages?.features?.htmlScripts || { isEnabled: false, scripts: [] }, [TemplateConfig]);
  const [htmlScriptsConsent, setHtmlScriptsConsent] = useState(htmlScripts);
  const { renderScripts } = useInjectScripts(htmlScriptsConsent);
  const isCompletionPage = value === 'Completion' && isLoanDepot;
  const [isNRinitialized, setIsNRinitialized] = useState(false);
  const [consentGiven, setConsentGiven] = useState(() => {
    const savedConsent = localStorage.getItem('newRelicConsent');
    return savedConsent === 'true';
  });
  const [shouldShowConsent, setShouldShowConsent] = useState(localStorage.getItem('newRelicConsent') == null && TemplateConfig?.pages?.features?.fullStory?.isHidden === false);

  // Get the current email from the form state
  const currentForm = useAppSelector((state) => state.CurrentForm);
  const userEmail = currentForm?.email;
  const isEmailValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(currentForm?.email);
  const isFullNameValid = Boolean(currentForm?.firstName?.trim()) && Boolean(currentForm?.lastName?.trim());
  const isPhoneValid = currentForm?.phone != null && /^\(\d{3}\)\s\d{3}-\d{4}$/.test(currentForm.phone);
  const isNewRelicEnabled = TemplateConfig?.pages?.features?.newRelic ?? false;
  const silentTrackingNewRelic = TemplateConfig?.pages?.features?.newRelic?.silentTracking ?? TemplateConfig?.pages?.features?.fullStory?.silentTracking ?? false;
  const templateConfigHasData = TemplateConfig && Object.keys(TemplateConfig).length > 0;
  const loanApplication = useAppSelector((state) => state.LoanApplication);
  const [isOpenResumeInitialized, setIsOpenResumeInitialized] = useState(false);

  useEffect(() => {
    setHtmlScriptsConsent(htmlScripts);
  }, [htmlScripts]);

  useEffect(() => {
    if (glanceStatus && glanceStatus.groupid && glanceStatus.site) {
      if (glanceStatus.isEnabled === undefined || glanceStatus.isActive === undefined) {
        dispatch(
          updateGlanceSettings({
            isEnabled: glanceStatus.isEnabled ?? true,
            isActive: glanceStatus.isActive ?? false,
          }),
        );
      }
    }
  }, [glanceStatus, dispatch]);

  useEffect(() => {
    if (isNewRelicEnabled && templateConfigHasData && !isNRinitialized && (consentGiven || silentTrackingNewRelic)) {
      initializeNewRelic(TemplateConfig, userEmail);
      setIsNRinitialized(true);
    }
  }, [isNewRelicEnabled, consentGiven, silentTrackingNewRelic, templateConfigHasData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isNewRelicEnabled && templateConfigHasData && isEmailValid && (consentGiven || silentTrackingNewRelic)) {
      updateNewRelicUser(userEmail);
    }
  }, [isNewRelicEnabled, userEmail, isEmailValid, silentTrackingNewRelic, templateConfigHasData]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isNewRelicEnabled && templateConfigHasData && isPhoneValid && (consentGiven || silentTrackingNewRelic)) {
      setCustomNewRelicAttribute('phone', currentForm.phone);
    }
  }, [isNewRelicEnabled, currentForm.phone, isPhoneValid, silentTrackingNewRelic, templateConfigHasData, consentGiven]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isNewRelicEnabled && templateConfigHasData && isFullNameValid && (consentGiven || silentTrackingNewRelic)) {
      const fullName = `${currentForm.firstName}${currentForm.middleName ? ` ${currentForm.middleName}` : ''} ${currentForm.lastName}${currentForm.suffix ? ` ${currentForm.suffix}` : ''}`;
      setCustomNewRelicAttribute('fullname', fullName);
    }
  }, [isNewRelicEnabled, currentForm.firstName, currentForm.middleName, currentForm.lastName, currentForm.suffix, silentTrackingNewRelic, templateConfigHasData, consentGiven]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isNewRelicEnabled && templateConfigHasData && loanApplication.loanNumber != null && (consentGiven || silentTrackingNewRelic)) {
      setCustomNewRelicAttribute('loanNumber', loanApplication.loanNumber);
    }
  }, [isNewRelicEnabled, loanApplication.loanNumber, silentTrackingNewRelic, templateConfigHasData, consentGiven]);

  // Initialize isOpenResume once when TemplateConfig is loaded
  useEffect(() => {
    if (templateConfigHasData && !isOpenResumeInitialized) {
      const shouldOpenResume = (TemplateConfig?.features?.resume?.isEnabled && TemplateConfig?.features?.resume?.version === 2) ?? false;
      dispatch(setIsOpenResume(shouldOpenResume));
      setIsOpenResumeInitialized(true);
    }
  }, [templateConfigHasData, isOpenResumeInitialized, TemplateConfig?.features?.resume?.isEnabled, TemplateConfig?.features?.resume?.version, dispatch]);

  useEffect(() => {
    if (glanceStatus?.groupid && glanceStatus?.site) {
      const existingScript = document.getElementById('glance-cobrowse');

      if (!existingScript) {
        const script = document.createElement('script');
        script.id = 'glance-cobrowse';
        script.type = 'text/javascript';
        script.async = true;
        script.dataset.groupid = glanceStatus.groupid;
        script.dataset.site = glanceStatus.site;
        script.src = `https://www.glancecdn.net/cobrowse/CobrowseJS.ashx?group=${glanceStatus.groupid}&site=${glanceStatus.site}`;

        script.onload = () => {
          dispatch(updateGlanceSettings({ isActive: true }));
        };

        script.onerror = () => {
          dispatch(updateGlanceSettings({ isActive: false }));
        };

        document.head.appendChild(script);
      }
    }
  }, [glanceStatus?.groupid, glanceStatus?.site]);

  useEffect(() => {
    if (TemplateConfig?.lenderId && isTemplateConfig) {
      setLoading(false);
    }
  }, [TemplateConfig, isTemplateConfig]);

  const handleConsent = (value: boolean) => {
    setConsentGiven(value);
    localStorage.setItem('newRelicConsent', value.toString());
    setShouldShowConsent(false);
  };

  (window as any).enableGlance = () => {
    if (glanceStatus?.groupid && glanceStatus?.site) {
      dispatch(updateGlanceSettings({ isEnabled: true, isActive: false }));
    }
  };

  (window as any).startGlanceSession = () => {
    if (glanceStatus?.isActive && (window as any).GLANCE) {
      try {
        (window as any).GLANCE.Cobrowse.Visitor.startSession();
      } catch (error) {
        console.error('Failed to start Glance session:', error);
      }
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const loId = urlParams.get('loId');
    const refId = urlParams.get('refId');
    const mhltid = urlParams.get('mhltid');

    const updatePayload: Record<string, string> = {};
    if (loId) updatePayload.loId = loId;
    if (refId) updatePayload.refId = refId;
    if (mhltid) updatePayload.leadId = mhltid;

    if (Object.keys(updatePayload).length > 0) {
      dispatch(updateLoanApplication(updatePayload));
    }
  }, [dispatch]);

  return (
    <>
      <Helmet>
        {favicon && <link rel="icon" href={favicon} />}
        {renderScripts()}
      </Helmet>
      {shouldShowConsent && <NewRelicConsent onConsent={handleConsent} userEmail={userEmail} />}
      <WrapperLoading $loading={loading}>
        <Spinner $loading={loading} />
      </WrapperLoading>
      <AppContainer $loading={loading}>
        {!isCompletionPage && <SideBar variant="primary" />}
        <ContentContainer $isCompletionPage={isCompletionPage}>
          <NavBar variant="primary" backgroundColor={TemplateConfig?.globalTheme?.backgroundColor} />
          <MainContent variant="primary" />
          <Footer variant="primary" />
        </ContentContainer>
      </AppContainer>
    </>
  );
};

export default App;
